<template>
  <v-dialog
    :value="show"
    width="700"
    :scrollable="false"
    @click:outside="handleClose"
    @keydown.esc="handleClose"
  >
    <v-card class="ma-0 pa-0">
      <v-card-title>{{ $t('assignee_selector.user_selection') }} </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pb-0 mb-3">
        <v-row class="ma-0 pt-3">
          <v-col cols="6">
            <div class="d-flex justify-space-between align-end">
              <span
                class="subtitle-2 font-weight-medium blue-grey--text text--darken-3"
              >
                {{ $t('assignee_selector.user_other') }}
              </span>
              <SearchScopeSelector
                data-tourid="site-global-toggle"
                :isLoading="isLoading"
                :initialScope="searchScope"
                @change="handleChangeSearchOrigin"
              />
            </div>
          </v-col>
          <v-col cols="6">
            <div class="d-flex justify-space-between align-end">
              <span
                v-if="hasUsersSelected"
                class="subtitle-2 font-weight-medium blue-grey--text text--darken-3"
              >
                {{ selectedText }}
              </span>
              <span
                v-if="hasUsersSelected"
                class="cursor-pointer primary--text"
                @click="onClearAll"
              >
                {{ $t('common.clear_all') }}
              </span>
            </div>
          </v-col>
        </v-row>
        <v-row class="ma-0 pa-0">
          <v-col cols="6">
            <v-sheet
              rounded
              outlined
              class="mt-4 sheet"
              data-tourid="advanced-assignee-selector-unselected-users"
            >
              <v-text-field
                dense
                clearable
                hide-details
                class="user-search-bar pa-2 mt-0"
                prepend-inner-icon="mdi-magnify"
                :disabled="isLoading"
                :placeholder="$t('common.search')"
                v-model="searchTerm"
              >
              </v-text-field>

              <v-list dense color="transparent" class="pa-1">
                <div
                  v-if="isLoading && !loadingMoreItems"
                  class="search-advanced-skeletons"
                >
                  <v-skeleton-loader
                    type="list-item-avatar-two-line,
                    list-item-avatar-two-line,
                    list-item-avatar-two-line,
                    list-item-avatar-two-line,
                    list-item-avatar-two-line,
                    list-item-avatar-two-line,
                    list-item-avatar-two-line"
                  />
                </div>
                <div v-else>
                  <div v-for="item in unselectedItems" :key="item.id">
                    <AccountListItem
                      v-if="!item.isTeam"
                      class="pl-1 pr-3 py-0"
                      :account="item"
                      :searchTerm="searchTerm"
                      @click="onSelect"
                    />
                    <TeamListItem
                      v-else
                      class="pl-1 pr-3 py-0"
                      :team="item"
                      :searchTerm="searchTerm"
                      @click="onSelect"
                    />
                  </div>
                  <div class="d-flex justify-center">
                    <Button
                      v-if="moreResults"
                      plain
                      :text="$t('common.load_more')"
                      :loading="loadingMoreItems"
                      :disabled="loadingMoreItems"
                      @click="getMoreItems"
                    />
                  </div>
                </div>
              </v-list>
            </v-sheet>
          </v-col>
          <v-col cols="6">
            <v-sheet
              rounded
              outlined
              class="sheet mt-4"
              data-tourid="advanced-assignee-selector-selected-users"
              :class="{
                'd-flex align-center justify-center': !hasUsersSelected,
              }"
            >
              <div v-if="hasUsersSelected">
                <v-text-field
                  dense
                  clearable
                  hide-details
                  class="user-search-bar pa-2 mt-0"
                  prepend-inner-icon="mdi-magnify"
                  :placeholder="$t('common.search')"
                  v-model="localSearchTerm"
                >
                </v-text-field>

                <v-list dense color="transparent" class="pa-0 ma-0">
                  <div v-for="item in selectedItems" :key="item.id">
                    <AccountListItem
                      v-if="!item.isTeam"
                      class="pl-1 pr-3 py-0"
                      :account="item"
                      :searchTerm="localSearchTerm"
                      @click="onRemove"
                    />
                    <TeamListItem
                      v-else
                      class="pl-1 pr-3 py-0"
                      :team="item"
                      :searchTerm="localSearchTerm"
                      @click="onRemove"
                    />
                  </div>
                </v-list>
              </div>
              <div
                v-if="hasUserSelectedWithResults"
                class="d-flex align-center justify-center"
                style="height: calc(100% - 70px)"
              >
                <EmptyState
                  class="px-6"
                  icon="mdi-ghost-outline"
                  :iconSize="32"
                  :title="$t('common.no_results_found')"
                  :message="$t('common.no_matches_text')"
                />
              </div>
              <EmptyState
                v-if="!hasUsersSelected"
                icon="mdi-ghost-outline"
                :title="$t('assignee_selector.no_users_selected_title')"
                :message="$t('assignee_selector.no_users_selected_message')"
              />
            </v-sheet>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="justify-end py-4">
        <Button
          plain
          color="grey"
          class="me-6"
          :text="$t('common.button.cancel')"
          @click="handleClose"
        />
        <Button
          plain
          class="me-5"
          color="primary"
          :text="$t('common.select')"
          @click="handleSelect"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import Button from '@/components/UI/Button'
import api from '@/api'
import { mapGetters } from 'vuex'
import _ from 'lodash'

import { fuseSearch } from '@/lib/fuseSearch'
import mixin from '@/mixins/mixin.vue'

import EmptyState from '@/components/EmptyState.vue'
import SearchScopeSelector from '@/components/SearchScopeSelector.vue'

import AccountListItem from './AccountListItem.vue'
import TeamListItem from './TeamListItem.vue'

export default {
  name: 'AdvancedAssigneeSelectorModal',

  mixins: [mixin],

  components: {
    SearchScopeSelector,
    AccountListItem,
    TeamListItem,
    Button,
    EmptyState,
  },

  props: {
    show: Boolean,
    includeTeams: Boolean,
    initialAccounts: {
      type: Array,
      default: () => [],
    },
    blacklist: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => {},
    },
    maxSelectableAccounts: {
      type: Number,
      default: null,
    },
  },

  async mounted() {
    this.getInitialAccounts()
    await this.searchItems()
  },

  data() {
    return {
      searchScope: this.$constants.SEARCH_SCOPES.site,
      searchTerm: '',
      localSearchTerm: '',
      loadPage: 0,
      isLoading: false,
      loadingMoreItems: false,
      moreResults: false,
      cachedAccounts: {},
      availableAccounts: [],
      availableTeams: [],
      selectedAccountIds: [],
      initialLoadExecuted: false,
    }
  },

  computed: {
    ...mapGetters(['account', 'organizationId']),

    selectedAccounts() {
      return this.selectedAccountIds.length
        ? this.selectedAccountIds.map(ids => this.cachedAccounts[ids])
        : []
    },

    selectedItems() {
      if (!this.selectedAccounts.length) return []

      if (this.localSearchTerm) {
        return fuseSearch(
          this.selectedAccounts,
          ['title'],
          this.localSearchTerm
        )
      }

      const selectedAccounts = _.sortBy(
        _.compact(this.selectedAccounts),
        item => item?.title?.toLowerCase(),
        'asc'
      )

      if (this.selectedAccountIds.includes(this.account.id)) {
        return _.uniqBy([this.account, ...selectedAccounts], 'id')
      }

      return selectedAccounts
    },

    unselectedItems() {
      const accounts = _.differenceBy(
        _.uniqBy([this.account, ...this.availableAccounts], 'id'),
        this.selectedAccounts,
        'id'
      )

      const accountsOmitingBlacklist = this.blacklist.length
        ? accounts.filter(account => !this.blacklist.includes(account.id))
        : accounts

      const parsedAccounts = this.parseAccounts(
        _.compact(accountsOmitingBlacklist)
      )
      const topScoreAccounts = parsedAccounts.slice(0, 5)
      const mergedAccounts = _.flatten(
        _.zip(parsedAccounts.slice(5), this.parseTeams(this.availableTeams))
      )
      return _.compact([...topScoreAccounts, ...mergedAccounts])
    },

    hasUsersSelected() {
      return !_.isEmpty(this.selectedAccountIds)
    },

    selectedText() {
      return _.isEmpty(this.selectedAccountIds)
        ? this.$t('assignee_selector.users_selected')
        : this.$t('assignee_selector.users_selected_count', {
            count: this.selectedAccountIds.length,
          })
    },

    hasUserSelectedWithResults() {
      return _.isEmpty(this.selectedItems) && this.localSearchTerm
    },
  },

  methods: {
    addAccountsToCache(accounts) {
      const parsedNewAccounts = this.parseAccounts(accounts)
      const hashedAccounts = _.keyBy(parsedNewAccounts, 'id')

      this.cachedAccounts = { ...this.cachedAccounts, ...hashedAccounts }
    },

    handleChangeSearchOrigin(searchScope) {
      if (this.isLoading) return
      this.searchScope = searchScope
      this.availableAccounts = []
      this.availableTeams = []
      this.loadPage = 0
      this.searchItems(this.searchTerm)
    },

    onRemove(element) {
      this.selectedAccountIds = this.selectedAccountIds.filter(
        accountId => accountId !== element._id
      )
    },

    onClearAll() {
      this.localSearchTerm = ''
      this.selectedAccountIds = []
    },

    onSelect(element) {
      const users = element.isTeam
        ? this.getAccountIdsFromTeam(element)
        : [element._id]

      if (
        this.maxSelectableAccounts &&
        this.selectedAccountIds.length >= this.maxSelectableAccounts
      ) {
        this.showError(
          this.$t('common.selection_limit_error', {
            selectionLimit: this.maxSelectableAccounts,
            type: this.$t('common.users.user_other'),
          })
        )
      }

      if (this.maxSelectableAccounts) {
        return (this.selectedAccountIds = _.take(
          _.uniq([...this.selectedAccountIds, ...users]),
          this.maxSelectableAccounts
        ))
      }

      this.selectedAccountIds = _.uniq([...this.selectedAccountIds, ...users])
    },

    getAccountIdsFromTeam(team) {
      const teamLeaders =
        team.leaders
          ?.filter(leader => !leader.isDeleted)
          ?.map(leader => leader.id) ?? []
      const teamMembers =
        team.members
          ?.filter(member => !member.isDeleted)
          ?.map(member => member.id) ?? []
      return [...teamLeaders, ...teamMembers]
    },

    parseAccounts(accounts) {
      if (!accounts) return

      return accounts?.map?.(account => ({
        ...account,
        id: account?._id ?? account?.id,
        photo: account?.photo,
        title: this.getFullName(account),
        subtitle: account?.position,
      }))
    },

    parseTeams(teams) {
      if (!teams) return

      return teams?.map?.(team => {
        const totalMembers = this.getTeamTotalMembers(team)

        return {
          ...team,
          isTeam: true,
          title: team.name,
          subtitle: `${totalMembers} ${this.$t('common.member', {
            count: totalMembers,
          })}`,
        }
      })
    },

    getTeamTotalMembers(team) {
      return (team?.leaders?.length ?? 0) + (team?.members?.length ?? 0)
    },

    async searchItems(terms = '') {
      try {
        this.isLoading = true
        await Promise.all([this.searchAccounts(terms), this.searchTeams(terms)])
      } catch (err) {
        this.showError(err)
      } finally {
        this.isLoading = false
        this.loadingMoreItems = false
      }
    },

    getMoreItems() {
      this.loadingMoreItems = true
      this.loadPage += 1
      this.searchItems(this.searchTerm)
    },

    async searchTeams(terms = '') {
      if (!this.includeTeams) return
      const params =
        this.searchScope === this.$constants.SEARCH_SCOPES.site
          ? { filters: { sites: [this.account?.site?._id] }, terms }
          : { terms }

      const response = await api.teams.getTeamsStream(this.organizationId, {
        ...params,
        sort: {
          by: terms?.length ? '_score' : 'name',
          order: terms?.length ? 'desc' : 'asc',
        },
        limit: 100,
        from: 100 * this.loadPage,
      })
      const fetchedTeams =
        response?.data?.results.map(result => result.team) ?? []
      this.availableTeams = [...this.availableTeams, ...fetchedTeams]
    },

    async searchAccounts(terms = '') {
      const filters = { isDeleted: false }

      if (this.searchScope === this.$constants.SEARCH_SCOPES.site) {
        filters.site = this.account?.site?._id
      }

      const response = await api.account.getAccounts(this.organizationId, {
        filters: { ...filters, ...this.filters },
        terms,
        limit: 100,
        from: 100 * this.loadPage,
        sort: {
          by: terms?.length ? '_score' : 'displayname',
          order: terms?.length ? 'desc' : 'asc',
        },
      })
      this.availableAccounts = [
        ...this.availableAccounts,
        ...(response?.data?.results ?? []),
      ]
      this.moreResults =
        this.availableAccounts.length < response.data.totalRecordsInData
    },

    async getInitialAccounts() {
      if (!this.initialAccounts.length) return
      const hashedAccounts = _.keyBy(this.initialAccounts, 'id')
      this.cachedAccounts = { ...this.cachedAccounts, ...hashedAccounts }
      this.selectedAccountIds = this.initialAccounts.map(account => account.id)
    },

    resetData() {
      this.searchScope = this.$constants.SEARCH_SCOPES.site
      this.searchTerm = ''
      this.localSearchTerm = ''
      this.isLoading = false
      this.cachedAccounts = {}
      this.availableAccounts = []
      this.availableTeams = []
      this.selectedAccountIds = []
      this.initialLoadExecuted = false
    },

    handleClose() {
      this.$emit('close')
      this.resetData()
    },

    handleSelect() {
      this.$emit('select', {
        ids: this.selectedAccountIds,
        items: this.selectedItems,
      })
      this.resetData()
    },
  },

  watch: {
    searchTerm: _.debounce(function(term) {
      this.availableAccounts = []
      this.availableTeams = []
      this.loadPage = 0
      this.searchItems(term)
    }, 500),

    availableAccounts(newAccounts) {
      this.addAccountsToCache(newAccounts)
      this.$emit('cacheUpdate', newAccounts)
    },

    availableTeams(newTeams) {
      const teamAccounts = newTeams.reduce((result, team) => {
        return [...result, ...team.leaders, ...team.members]
      }, [])

      this.addAccountsToCache(teamAccounts)
      this.$emit('cacheUpdate', teamAccounts)
    },
  },
}
</script>
<style lang="scss">
.user-search-bar {
  font-size: 12px !important;
  position: sticky;
  top: 0;
  background-color: $color-white;
  z-index: 1;
  .v-input__slot::after {
    border-style: none !important;
  }
  .v-input__slot::before {
    border-style: none !important;
  }
  .v-input__slot .v-text-field__slot input {
    padding-top: 0;
  }
  .v-input__append-inner button.v-icon {
    color: $color-blue-grey !important;
  }
  .v-input__prepend-inner i.v-icon {
    color: $color-blue-grey;
  }
}

.sheet {
  height: 500px !important;
  overflow-y: auto !important;
}

.search-advanced-skeletons
  .v-skeleton-loader
  .v-skeleton-loader__list-item-avatar-two-line {
  height: 40px;
  margin: 4px 0;
  .v-skeleton-loader__avatar {
    margin-left: -4px;
    margin-right: 8px;
    width: 24px;
    height: 24px;
  }
}
</style>
